import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Slider,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { PlayArrow, Pause, FastForward, FastRewind } from "@mui/icons-material";

const AudioPlayer = ({ audio }) => {
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (msg) => {
    setErrorMessage(msg);
    setErrorOpen(true);
    setIsPlaying(false);
  };

  const handleErrorClose = (_, reason) => {
    if (reason === "clickaway") return;
    setErrorOpen(false);
  };

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration || 0);
      audioElement.playbackRate = playbackRate;
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audioElement.addEventListener("timeupdate", handleTimeUpdate);
    audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    audioElement.addEventListener("play", handlePlay);
    audioElement.addEventListener("pause", handlePause);
    audioElement.addEventListener("ended", handleEnded);

    return () => {
      audioElement.removeEventListener("timeupdate", handleTimeUpdate);
      audioElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audioElement.removeEventListener("play", handlePlay);
      audioElement.removeEventListener("pause", handlePause);
      audioElement.removeEventListener("ended", handleEnded);
    };
  }, [playbackRate, audio]);

  const togglePlayPause = async () => {
    const el = audioRef.current;
    if (!el) return;

    if (el.paused) {
      try {
        await el.play();
      } catch (e) {
        console.error("Play failed", e);
        showError(
          "Cannot play this audio. File may be corrupted or unsupported.",
        );
      }
    } else {
      el.pause();
    }
  };

  const skipForward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(
      audioRef.current.currentTime + 10,
      duration,
    );
  };

  const skipBackward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(
      audioRef.current.currentTime - 10,
      0,
    );
  };

  const handleSpeedChange = (_, newValue) => {
    setPlaybackRate(newValue);

    if (audioRef.current) {
      audioRef.current.playbackRate = newValue;
    }
  };

  const handleSeek = (_, newValue) => {
    if (!audioRef.current || !duration) return;

    const seekTime = (newValue / 100) * duration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds)) return "00:00";

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <Box sx={{ width: 640, mx: "auto", p: 2 }}>
      <audio
        ref={audioRef}
        src={audio}
        preload="metadata"
        style={{ display: "none" }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={togglePlayPause}
          variant="contained"
          sx={{ backgroundColor: "#f50057" }}
        >
          {isPlaying ? (
            <Pause sx={{ color: "#fff" }} />
          ) : (
            <PlayArrow sx={{ color: "#fff" }} />
          )}
        </Button>

        <Button
          onClick={skipBackward}
          variant="contained"
          sx={{ backgroundColor: "#f50057" }}
        >
          <FastRewind sx={{ color: "#fff" }} />
        </Button>

        <Button
          onClick={skipForward}
          variant="contained"
          sx={{ backgroundColor: "#f50057" }}
        >
          <FastForward sx={{ color: "#fff" }} />
        </Button>

        <Typography sx={{ color: "#fff" }}>Speed:</Typography>

        <Slider
          value={playbackRate}
          onChange={handleSpeedChange}
          min={0.5}
          max={2}
          step={0.1}
          sx={{ width: 100, mx: 2, color: "#f50057" }}
        />

        <Typography sx={{ color: "#fff" }}>{playbackRate}x</Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
        <Typography sx={{ color: "#fff" }}>
          {formatTime(currentTime)}
        </Typography>

        <Slider
          value={duration ? (currentTime / duration) * 100 : 0}
          onChange={handleSeek}
          sx={{ mx: 2, flexGrow: 1, color: "#f50057" }}
        />

        <Typography sx={{ color: "#fff" }}>{formatTime(duration)}</Typography>
      </Box>

      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleErrorClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AudioPlayer;
